import Flex from "@/components/Flex";
import SuccessModal from "@/components/SuccessModal";
import UploadData from "@/components/app/admin/Library/UploadData";
import type { NavDataType } from "@/constants/common";
import AddPatientDrawer from "@/pages/app/provider/patient/AddPatient";
import InstantAppointMentModal from "@/pages/app/provider/scheduling/InstantAppointMentModal";
import NewAppointment from "@/pages/app/provider/scheduling/appointment/NewAppointment";
import { FallBackComp } from "@/pages/app/provider/scheduling/appointment/encounter/content-section/IntakeFormData";
import lazyWithRetry from "@/utils/lazy-with-retry";
import { AppBar, Divider, IconButton, Tooltip } from "@mui/material";
import { Suspense, useState, type ReactNode } from "react";
import AppSwitcherPopOver from "./components/AppSwitcherPopOver";
import NavAddPatientModal from "./components/NavAddPatientModal";
import NavBarBrand from "./components/NavBarBrand";
import NavBarCreateMenu from "./components/NavBarCreateMenu";
import NavBarProfileMenu from "./components/NavBarProfileMenu";
import NavBarTabs from "./components/NavBarTabs";
import { useNavBarCreateFlows } from "./hooks/use-nav-bar-create-flows";
import { useNavBarSession } from "./hooks/use-nav-bar-session";
import {
  NAV_BAR_ASSETS,
  NAV_BAR_COLORS,
  NAV_BAR_GUTTER_PX,
  NAV_BAR_HEIGHT_PX,
  NAV_BAR_LABELS,
  NAV_BAR_LAYOUT,
} from "./nav-bar-constant";
import type { NavBarProps } from "./nav-bar-types";
import Notification from "./notification/Notification";

const SpotLight = lazyWithRetry(() => import("@/components/app/SpotLight"));

export const NAVBAR_HEIGHT = `${NAV_BAR_HEIGHT_PX}px`;

export const NAVBAR_HEIGHT_PLUS15 = `${NAV_BAR_HEIGHT_PX + NAV_BAR_GUTTER_PX}px`;

interface BarIconButtonProps {
  title: string;
  children: ReactNode;
  onClick?: () => void;
}

export const BarIconButton = ({
  title,
  children,
  onClick,
}: BarIconButtonProps) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      aria-label={title}
      sx={{ color: NAV_BAR_COLORS.ON_BAR }}
      onClick={onClick}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const NavBar = ({
  view,
  navOptions,
  profileData,
  active,
  anchorElUser,
  onClickMenuItem,
  handleCloseUserMenu,
  handleProfileClick,
}: NavBarProps) => {
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
  const { user, isProviderView, providerGroup, loggedLocation } =
    useNavBarSession(view);
  const flows = useNavBarCreateFlows();

  const handleTabClick = (
    tab: NavDataType,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setOpenMenuFor(tab.children?.length ? tab.value : null);
    onClickMenuItem(tab, event);
  };

  const handleCloseSubMenu = (subMenuItem?: NavDataType) => {
    setOpenMenuFor(null);
    handleCloseUserMenu(subMenuItem);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        zIndex: NAV_BAR_LAYOUT.BAR_Z_INDEX,
        height: NAVBAR_HEIGHT,
        px: NAV_BAR_LAYOUT.BAR_PADDING_X,
        py: 0,
        boxShadow: "none !important",
        backgroundColor: NAV_BAR_COLORS.BAR_BG,
      }}
    >
      <Flex
        alignItems="stretch"
        height="100%"
        gap={NAV_BAR_LAYOUT.BRAND_TO_TABS_GAP}
      >
        <Flex alignItems="center" flexShrink={0}>
          <NavBarBrand
            providerGroups={providerGroup ? [providerGroup] : []}
            activeProviderGroup={providerGroup}
          />
        </Flex>

        <Flex flex={1} minWidth={0} alignItems="flex-end">
          <NavBarTabs
            navOptions={navOptions}
            active={active}
            anchorEl={anchorElUser}
            openMenuFor={openMenuFor}
            onTabClick={handleTabClick}
            onCloseMenu={handleCloseSubMenu}
          />
        </Flex>

        <Flex
          alignItems="center"
          gap={NAV_BAR_LAYOUT.ACTIONS_GAP}
          flexShrink={0}
        >
          {isProviderView && (
            <>
              <BarIconButton
                title={NAV_BAR_LABELS.SEARCH_TOOLTIP}
                onClick={flows.spotLight.open}
              >
                <img
                  src={NAV_BAR_ASSETS.SEARCH_ICON}
                  alt=""
                  width={NAV_BAR_LAYOUT.SEARCH_ICON_SIZE}
                  height={NAV_BAR_LAYOUT.SEARCH_ICON_SIZE}
                />
              </BarIconButton>

              <NavBarCreateMenu onSelect={flows.startCreateAction} />
            </>
          )}

          <Notification />

          <NavBarProfileMenu
            user={user}
            profileData={profileData}
            providerGroup={providerGroup}
            loggedLocation={loggedLocation}
            onProfileClick={handleProfileClick}
          />

          <Divider
            orientation="vertical"
            flexItem
            sx={{ my: 1.5, borderColor: NAV_BAR_COLORS.DIVIDER }}
          />

          <AppSwitcherPopOver />
        </Flex>
      </Flex>

      {isProviderView && (
        <>
          <Suspense fallback={<FallBackComp />}>
            <SpotLight disclosureHost={flows.spotLight} />
          </Suspense>

          <InstantAppointMentModal disclosureHost={flows.instantAppointment} />

          <NavAddPatientModal
            disclosureHost={flows.addPatientSource}
            onNextClick={flows.selectAddPatientSource}
          />

          <AddPatientDrawer disclosureHost={flows.addPatient} />

          {flows.newAppointment.opened && (
            <NewAppointment
              opened={flows.newAppointment.opened}
              onClose={flows.newAppointment.close}
            />
          )}

          <SuccessModal
            label={NAV_BAR_LABELS.EHR_INVITE_SUCCESS}
            opened={flows.ehrInviteSent}
            onClose={flows.closeEhrInviteSent}
          />

          <UploadData
            modalTitle={NAV_BAR_LABELS.IMPORT_PATIENTS_TITLE}
            open={flows.uploadPatients.opened}
            onClose={flows.uploadPatients.close}
          />
        </>
      )}
    </AppBar>
  );
};

export default NavBar;
